import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';

const CandidateDetailRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(20);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [employerFilter, setEmployerFilter] = useState('');
  const [jobTitleFilter, setJobTitleFilter] = useState('');
  
  // Approval state
  const [approvingRequestId, setApprovingRequestId] = useState(null);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [approvingSelected, setApprovingSelected] = useState(false);
  const [rejectingSelected, setRejectingSelected] = useState(false);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [adminNotes, setAdminNotes] = useState({});

  useEffect(() => {
    fetchRequests();
  }, [page, statusFilter, employerFilter, jobTitleFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize
      };
      
      if (statusFilter) params.status_filter = statusFilter;
      if (employerFilter) params.employer_filter = employerFilter;
      if (jobTitleFilter) params.job_title_filter = jobTitleFilter;
      
      const response = await adminAPI.getCandidateDetailRequests(params);
      const data = response.data;
      const loadedRequests = data.requests || [];
      setRequests(loadedRequests);
      setTotal(data.total || 0);
      setSelectedRequestIds((prev) => {
        const availablePendingIds = new Set(
          loadedRequests.filter((request) => request.status === 'pending').map((request) => request.id)
        );
        return prev.filter((id) => availablePendingIds.has(id));
      });
    } catch (error) {
      console.error('Error fetching candidate detail requests:', error);
      toast.error('Failed to load candidate detail requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!window.confirm('Are you sure you want to approve this request?')) {
      return;
    }

    setApprovingRequestId(requestId);
    try {
      await adminAPI.approveCandidateDetailRequest(requestId, adminNotes[requestId] || '');
      toast.success('Request approved successfully');
      setAdminNotes(prev => {
        const newNotes = { ...prev };
        delete newNotes[requestId];
        return newNotes;
      });
      await fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setApprovingRequestId(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('Are you sure you want to reject this request?')) {
      return;
    }

    setRejectingRequestId(requestId);
    try {
      await adminAPI.rejectCandidateDetailRequest(requestId, adminNotes[requestId] || '');
      toast.success('Request rejected');
      setAdminNotes(prev => {
        const newNotes = { ...prev };
        delete newNotes[requestId];
        return newNotes;
      });
      await fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setRejectingRequestId(null);
    }
  };

  const handleToggleSelectRequest = (requestId) => {
    setSelectedRequestIds((prev) =>
      prev.includes(requestId)
        ? prev.filter((id) => id !== requestId)
        : [...prev, requestId]
    );
  };

  const handleToggleSelectAllPending = () => {
    const pendingIds = requests
      .filter((request) => request.status === 'pending')
      .map((request) => request.id);

    if (pendingIds.length === 0) {
      return;
    }

    const allPendingSelected = pendingIds.every((id) => selectedRequestIds.includes(id));
    if (allPendingSelected) {
      setSelectedRequestIds((prev) => prev.filter((id) => !pendingIds.includes(id)));
    } else {
      setSelectedRequestIds((prev) => Array.from(new Set([...prev, ...pendingIds])));
    }
  };

  const handleApproveSelected = async () => {
    const pendingRequests = requests.filter(
      (request) => request.status === 'pending' && selectedRequestIds.includes(request.id)
    );

    if (pendingRequests.length === 0) {
      toast('Select pending requests to approve');
      return;
    }

    if (!window.confirm(`Are you sure you want to approve ${pendingRequests.length} selected request(s)?`)) {
      return;
    }

    setApprovingSelected(true);
    try {
      const results = await Promise.allSettled(
        pendingRequests.map((request) =>
          adminAPI.approveCandidateDetailRequest(request.id, adminNotes[request.id] || '')
        )
      );

      const succeeded = results.filter((result) => result.status === 'fulfilled').length;
      const failed = pendingRequests.length - succeeded;

      if (succeeded > 0) {
        setAdminNotes((prev) => {
          const newNotes = { ...prev };
          pendingRequests.forEach((request) => {
            delete newNotes[request.id];
          });
          return newNotes;
        });
        setSelectedRequestIds((prev) => prev.filter((id) => !pendingRequests.some((request) => request.id === id)));
      }

      if (failed === 0) {
        toast.success(`Approved ${succeeded} request${succeeded > 1 ? 's' : ''} successfully`);
      } else {
        toast.error(`Approved ${succeeded}, failed ${failed}. Please retry failed requests.`);
      }

      await fetchRequests();
    } catch (error) {
      console.error('Error approving selected requests:', error);
      toast.error('Failed to approve selected requests');
    } finally {
      setApprovingSelected(false);
    }
  };

  const handleRejectSelected = async () => {
    const pendingRequests = requests.filter(
      (request) => request.status === 'pending' && selectedRequestIds.includes(request.id)
    );

    if (pendingRequests.length === 0) {
      toast('Select pending requests to reject');
      return;
    }

    if (!window.confirm(`Are you sure you want to reject ${pendingRequests.length} selected request(s)?`)) {
      return;
    }

    setRejectingSelected(true);
    try {
      const results = await Promise.allSettled(
        pendingRequests.map((request) =>
          adminAPI.rejectCandidateDetailRequest(request.id, adminNotes[request.id] || '')
        )
      );

      const succeeded = results.filter((result) => result.status === 'fulfilled').length;
      const failed = pendingRequests.length - succeeded;

      if (succeeded > 0) {
        setAdminNotes((prev) => {
          const newNotes = { ...prev };
          pendingRequests.forEach((request) => {
            delete newNotes[request.id];
          });
          return newNotes;
        });
        setSelectedRequestIds((prev) => prev.filter((id) => !pendingRequests.some((request) => request.id === id)));
      }

      if (failed === 0) {
        toast.success(`Rejected ${succeeded} request${succeeded > 1 ? 's' : ''} successfully`);
      } else {
        toast.error(`Rejected ${succeeded}, failed ${failed}. Please retry failed requests.`);
      }

      await fetchRequests();
    } catch (error) {
      console.error('Error rejecting selected requests:', error);
      toast.error('Failed to reject selected requests');
    } finally {
      setRejectingSelected(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const pendingCount = requests.filter((request) => request.status === 'pending').length;
  const selectedPendingCount = requests.filter(
    (request) => request.status === 'pending' && selectedRequestIds.includes(request.id)
  ).length;
  const allPendingSelected = pendingCount > 0 && selectedPendingCount === pendingCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Candidate Detail Requests</h2>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={allPendingSelected}
                onChange={handleToggleSelectAllPending}
                disabled={pendingCount === 0}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
              />
              Select pending
            </label>
            <button
              onClick={handleApproveSelected}
              disabled={approvingSelected || rejectingSelected || selectedPendingCount === 0}
              className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {approvingSelected ? 'Approving Selected...' : `Approve Selected (${selectedPendingCount})`}
            </button>
            <button
              onClick={handleRejectSelected}
              disabled={rejectingSelected || approvingSelected || selectedPendingCount === 0}
              className="px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {rejectingSelected ? 'Rejecting Selected...' : `Reject Selected (${selectedPendingCount})`}
            </button>
            <button
              onClick={fetchRequests}
              className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employer</label>
            <input
              type="text"
              placeholder="Filter by employer..."
              value={employerFilter}
              onChange={(e) => {
                setEmployerFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
            <input
              type="text"
              placeholder="Filter by job title..."
              value={jobTitleFilter}
              onChange={(e) => {
                setJobTitleFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="ui-card">
        {requests.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500">No candidate detail requests found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {requests.map((request) => (
              <div key={request.id} className="p-4 md:p-5 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {request.status === 'pending' && (
                        <input
                          type="checkbox"
                          checked={selectedRequestIds.includes(request.id)}
                          onChange={() => handleToggleSelectRequest(request.id)}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      )}
                      <h3 className="text-base md:text-lg font-semibold text-gray-900">
                        {request.candidateName || 'Unknown Candidate'}
                      </h3>
                      {getStatusBadge(request.status)}
                    </div>

                    {request.status === 'pending' ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                        <div className="whitespace-nowrap">
                          <span className="font-medium">Employer:</span> {request.employerName || 'Unknown'}
                        </div>
                        <div className="whitespace-nowrap">
                          <span className="font-medium">Requested:</span> {formatDate(request.requestedAt)}
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          <button
                            onClick={() => handleApprove(request.id)}
                            disabled={approvingRequestId === request.id}
                            className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {approvingRequestId === request.id ? 'Approving...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => handleReject(request.id)}
                            disabled={rejectingRequestId === request.id}
                            className="px-3 py-1.5 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {rejectingRequestId === request.id ? 'Rejecting...' : 'Reject'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Employer:</span> {request.employerName || 'Unknown'}
                        </div>
                        {request.jobTitle && (
                          <div>
                            <span className="font-medium">Job:</span> {request.jobTitle}
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Requested:</span> {formatDate(request.requestedAt)}
                        </div>
                        {request.approvedAt && (
                          <div>
                            <span className="font-medium">Approved:</span> {formatDate(request.approvedAt)}
                          </div>
                        )}
                        {request.rejectedAt && (
                          <div>
                            <span className="font-medium">Rejected:</span> {formatDate(request.rejectedAt)}
                          </div>
                        )}
                      </div>
                    )}

                    {request.adminNotes && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs md:text-sm text-gray-700">
                        <span className="font-medium">Admin Notes:</span> {request.adminNotes}
                      </div>
                    )}

                    {request.status === 'pending' && (
                      <div className="mt-2">
                        <textarea
                          placeholder="Add admin note (optional)..."
                          value={adminNotes[request.id] || ''}
                          onChange={(e) => setAdminNotes(prev => ({ ...prev, [request.id]: e.target.value }))}
                          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          rows={1}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} requests
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {Math.ceil(total / pageSize)}
              </span>
              <button
                onClick={() => setPage(p => Math.min(Math.ceil(total / pageSize), p + 1))}
                disabled={page >= Math.ceil(total / pageSize)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDetailRequests;

